import React, { useEffect, useRef, useState } from 'react';
import { WifiOff, RefreshCw, Wifi } from 'lucide-react';

interface ConnectionBannerProps {
  /** 소켓 연결 상태 (App.tsx의 isConnected) */
  isConnected: boolean;
}

/** 끊김을 이만큼 지속해야 배너를 띄운다 — 부팅 직후·순간 재연결에서 깜빡이지 않게 */
const SHOW_DELAY_MS = 2500;
/** 복구 안내를 보여주는 시간 */
const RECOVERED_MS = 2000;

/**
 * 연결 상태 배너 (데스크톱·모바일 공용, 레이아웃 흐름 안에 얇게 들어간다).
 *
 * 두 신호를 본다:
 *  - navigator.onLine — 기기 자체가 오프라인 (즉시 표시)
 *  - 소켓 연결 여부 — 서버와 끊김 (SHOW_DELAY_MS 이상 지속될 때만 표시)
 *
 * 이전까지는 데스크톱 rail의 아바타 옆 3px 점 하나가 유일한 표시였고 모바일에는 아무것도 없었다.
 * 배너는 overlay가 아니라 flow에 두어 채팅 헤더나 입력창을 가리지 않는다.
 */
export const ConnectionBanner: React.FC<ConnectionBannerProps> = ({ isConnected }) => {
  const [isOnline, setIsOnline] = useState<boolean>(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [showDisconnected, setShowDisconnected] = useState(false);
  const [showRecovered, setShowRecovered] = useState(false);
  const wasShowingRef = useRef(false);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    if (isConnected) {
      setShowDisconnected(false);
      return;
    }
    const t = setTimeout(() => setShowDisconnected(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [isConnected]);

  const visible = !isOnline || showDisconnected;

  // 끊김 배너가 떠 있다가 복구되면 잠깐 "다시 연결됨"을 보여준다
  useEffect(() => {
    if (visible) {
      wasShowingRef.current = true;
      setShowRecovered(false);
      return;
    }
    if (!wasShowingRef.current) return;
    wasShowingRef.current = false;
    setShowRecovered(true);
    const t = setTimeout(() => setShowRecovered(false), RECOVERED_MS);
    return () => clearTimeout(t);
  }, [visible]);

  if (!visible && !showRecovered) return null;

  if (showRecovered) {
    return (
      <div data-component="connection-banner" role="status" aria-live="polite" className="shrink-0 flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 border-b border-green-100">
        <Wifi size={14} aria-hidden="true" />
        <span>다시 연결되었습니다</span>
      </div>
    );
  }

  return (
    <div data-component="connection-banner" role="status" aria-live="polite" className="shrink-0 flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium bg-brand-100 text-brand-800 border-b border-brand-200">
      {isOnline ? (
        <>
          <RefreshCw size={14} className="animate-spin" aria-hidden="true" />
          <span>서버와 연결이 끊겼습니다. 다시 연결하는 중…</span>
        </>
      ) : (
        <>
          <WifiOff size={14} aria-hidden="true" />
          <span>인터넷 연결이 없습니다. 연결되면 자동으로 이어집니다.</span>
        </>
      )}
    </div>
  );
};

export default ConnectionBanner;
